// components/OtherIncomeStep.tsx
import React from 'react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { Row, Col, Typography, Divider, Select, Input } from 'antd';
import { FormLabel, InputField, SelectField } from '@/components/Form';
import { ErrorText } from '@/components/Elements';
import RadioGroupField from '@/components/Form/RadioGroupField';
import { TAX_EMPLOYMENT_OPTIONS } from '@/constant/options';
import { currencies } from '@/utils/currenciesUtils';
import { PreLodgementChecklistFormValues } from '../types/preLodgementChecklist';

const YES_NO = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

const { Title, Text } = Typography;

type Props = {
  control: Control<PreLodgementChecklistFormValues>;
  error: FieldErrors<PreLodgementChecklistFormValues>;
};

export const OtherIncomeStep: React.FC<Props> = ({ control, error }) => {
  return (
    <>
      <div>
        <Title level={5} style={{ marginBottom: 4, marginTop: 0 }}>
          Other Australian Income
        </Title>
        <Text type="secondary">
          Please tick other forms of Australian income you have received.
        </Text>

        <Divider style={{ margin: '12px 0 20px' }} />
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <RadioGroupField
              label="Any dividends or interest?"
              name="dividendAnswer"
              control={control}
              options={YES_NO}
              error={error?.dividendAnswer?.message}
              cols={{ sm: 2, md: 2, lg: 2 }}
            />
          </Col>

          <Col xs={24}>
            <RadioGroupField
              label="Any crypto holdings?"
              name="cryptoAnswer"
              control={control}
              options={YES_NO}
              error={error?.cryptoAnswer?.message}
              cols={{ sm: 2, md: 2, lg: 2 }}
            />
          </Col>

          <Col xs={24}>
            <RadioGroupField
              label="Any share disposals?"
              name="shareDisposalAnswer"
              control={control}
              options={YES_NO}
              error={error?.shareDisposalAnswer?.message}
              cols={{ sm: 2, md: 2, lg: 2 }}
            />
          </Col>
        </Row>
      </div>
      <div>
        <Title level={5} style={{ marginBottom: 4, marginTop: 0 }}>
          Foreign Income
        </Title>
        <Text type="secondary">
          Foreign income derived from overseas sources.
        </Text>

        <Divider style={{ margin: '12px 0 20px' }} />

        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <SelectField
              formDirection="row"
              fullWidth={true}
              label="Foreign Income source"
              name="incomeSource"
              control={control}
              placeholder="Select income source"
              options={TAX_EMPLOYMENT_OPTIONS}
              required
              error={error?.incomeSource?.message ?? ''}
            />
          </Col>

          <Col xs={24}>
            <InputField
              formDirection="row"
              label="Employer"
              name="employer"
              control={control}
              placeholder="Company name (optional)"
              error={error?.employer?.message ?? ''}
            />
          </Col>

          <Col xs={24}>
            <div className="form-control row">
              <FormLabel
                required
                label="Approx. annual income for financial year"
              />
              <div className="input-containter">
                <Row gutter={[8, 0]}>
                  <Col xs={10} md={8}>
                    <Controller
                      name="annualSalaryCurrency"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={currencies}
                          showSearch
                          placeholder="AUD"
                          style={{ width: '100%' }}
                          status={
                            error?.annualSalaryCurrency ? 'error' : undefined
                          }
                          filterOption={(input, option) =>
                            Boolean(
                              option?.label
                                ?.toLowerCase()
                                .includes(input.toLowerCase()),
                            )
                          }
                        />
                      )}
                    />
                  </Col>
                  <Col xs={14} md={16}>
                    <Controller
                      name="annualSalary"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          value={
                            field.value
                              ? Number(
                                  String(field.value).replace(/,/g, ''),
                                ).toLocaleString()
                              : ''
                          }
                          onChange={(e) => {
                            const raw = e.target.value
                              .replace(/,/g, '')
                              .replace(/[a-zA-Z]/g, '');
                            field.onChange(isNaN(Number(raw)) ? '' : raw);
                          }}
                          placeholder="e.g. 120,000"
                          status={error?.annualSalary ? 'error' : undefined}
                          inputMode="decimal"
                        />
                      )}
                    />
                  </Col>
                </Row>
                {(error?.annualSalaryCurrency?.message ||
                  error?.annualSalary?.message) && (
                  <ErrorText
                    error={
                      error?.annualSalaryCurrency?.message ||
                      error?.annualSalary?.message ||
                      ''
                    }
                  />
                )}
              </div>
            </div>
          </Col>

          <Col xs={24}>
            <div className="form-control row">
              <FormLabel required label="Approx. bonuses and benefits" />
              <div className="input-containter">
                <Row gutter={[8, 0]}>
                  <Col xs={10} md={8}>
                    <Controller
                      name="bonusesAndBenefitsCurrency"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={currencies}
                          showSearch
                          placeholder="AUD"
                          style={{ width: '100%' }}
                          status={
                            error?.bonusesAndBenefitsCurrency
                              ? 'error'
                              : undefined
                          }
                          filterOption={(input, option) =>
                            Boolean(
                              option?.label
                                ?.toLowerCase()
                                .includes(input.toLowerCase()),
                            )
                          }
                        />
                      )}
                    />
                  </Col>
                  <Col xs={14} md={16}>
                    <Controller
                      name="bonusesAndBenefits"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          value={
                            field.value
                              ? Number(
                                  String(field.value).replace(/,/g, ''),
                                ).toLocaleString()
                              : ''
                          }
                          onChange={(e) => {
                            const raw = e.target.value
                              .replace(/,/g, '')
                              .replace(/[a-zA-Z]/g, '');
                            field.onChange(isNaN(Number(raw)) ? '' : raw);
                          }}
                          placeholder="e.g. 15,000"
                          status={
                            error?.bonusesAndBenefits ? 'error' : undefined
                          }
                          inputMode="decimal"
                        />
                      )}
                    />
                  </Col>
                </Row>
                {(error?.bonusesAndBenefitsCurrency?.message ||
                  error?.bonusesAndBenefits?.message) && (
                  <ErrorText
                    error={
                      error?.bonusesAndBenefitsCurrency?.message ||
                      error?.bonusesAndBenefits?.message ||
                      ''
                    }
                  />
                )}
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </>
  );
};
